/**
 * LandingPage — hero / welcome carousel for polkdesq.com.
 *
 * Design intent (Feb 2026): classic, minimal, feature-showy, no noise.
 * One headline, one mock "deadlines in 48 hours" panel that demonstrates
 * the product's core value (catching things before they slip), one CTA.
 *
 * Two modes:
 *   • Public (default): served at `/` to unauthenticated visitors. CTA →
 *     /login. Microsoft-SSO fineprint shown.
 *   • Authed (`isAuthed`): served at `/welcome` after a logged-in user
 *     opens the app via bookmark/saved login if it's been >1h since the
 *     last view on this device. CTA → /, auto-advances after 30s, no
 *     auth fineprint.
 *
 * Layout-only file: data + timer logic lives in `hooks/useLandingCarousel.js`,
 * the row markup lives in `components/LandingDeadlineRow.js`.
 */
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { MoonStar, SunMedium, ArrowUpRight } from 'lucide-react';
import { BrandMark } from '../components/BrandMark';
import { useTheme } from '../contexts/ThemeContext';
import { safeStorage } from '../utils/safeStorage';
import { minorToMajor } from '../utils/money';
import { LANDING_TIMESTAMP_KEY, WELCOME_AUTO_ADVANCE_SECONDS } from '../config/landingGate';
import { LandingDeadlineRow } from '../components/LandingDeadlineRow';
import {
  useRotatingTicker,
  useLiveBriefing,
  useWelcomeAutoAdvance,
} from '../hooks/useLandingCarousel';
import { briefingItemKey } from '../utils/briefingItemKey';
// Shared with the Briefing page (CONFIG-DRIFT-SWEEP-1 dedupe): item-type
// meta single-sources from commitmentConstants.ITEM_TYPE_CONFIG.
import { ITEM_TYPE_META, formatAmount, formatHoursUntil, tierOf } from './briefing/briefingUtils';

// Static showcase data — illustrative, NOT live user data. The shape mirrors
// the real Commitments/Calls payload so the panel is recognisable after sign-in.
// Each row carries a synthetic `hoursUntil` so the urgency heat-map runs
// on demo rows too.
const DEMO_DEADLINE_SETS = [
  [
    { id: 'a1', title: 'Court hearing prep',  workspace: 'Bizco LLC',   due: '18 hours', hoursUntil: 18 },
    { id: 'a2', title: 'Tax filing reminder', workspace: 'SunStrip',    due: '1 day',    hoursUntil: 24 },
    { id: 'a3', title: 'Client follow-up',    workspace: 'Apex AI',     due: '1 day 4h', hoursUntil: 28 },
    { id: 'a4', title: 'Payment due',         workspace: 'Veno Inc.',   due: '2 days',   hoursUntil: 48 },
  ],
  [
    { id: 'b1', title: 'Invoice signoff',     workspace: 'Apex AI',     due: '4 hours',  hoursUntil: 4  },
    { id: 'b2', title: 'Quarterly review',    workspace: 'Bizco LLC',   due: '14 hours', hoursUntil: 14 },
    { id: 'b3', title: 'Vendor renewal',      workspace: 'SunStrip',    due: '1 day',    hoursUntil: 24 },
    { id: 'b4', title: 'Compliance audit',    workspace: 'Veno Inc.',   due: '1 day 8h', hoursUntil: 32 },
  ],
  [
    { id: 'c1', title: 'Board meeting brief', workspace: 'Bizco LLC',   due: '6 hours',  hoursUntil: 6  },
    { id: 'c2', title: 'Lease renewal',       workspace: 'SunStrip',    due: '20 hours', hoursUntil: 20 },
    { id: 'c3', title: 'NDA expiry',          workspace: 'Apex AI',     due: '1 day 2h', hoursUntil: 26 },
    { id: 'c4', title: 'Insurance renewal',   workspace: 'Veno Inc.',   due: '2 days',   hoursUntil: 48 },
  ],
];

const ROTATION_SECONDS = 15;
// Thresholds (seconds) at which each progress dot lights up. 5 dots across
// 15s = one lights every 3s.
const ROTATION_DOT_THRESHOLDS = [0, 3, 6, 9, 12];

export default function LandingPage({ isAuthed = false } = {}) {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === 'night';

  const { secondsAgo, setIndex } = useRotatingTicker({
    rotationSeconds: ROTATION_SECONDS,
    setCount: DEMO_DEADLINE_SETS.length,
  });
  const sLabel = secondsAgo === 1 ? 'second' : 'seconds';

  const { liveItems, liveTotals, hasLiveData } = useLiveBriefing(isAuthed);
  const autoAdvanceLeft = useWelcomeAutoAdvance({
    isAuthed,
    navigate,
    seconds: WELCOME_AUTO_ADVANCE_SECONDS,
  });

  // Panel state machine (SAMPLE-DATA-CLEANUP-1): the demo carousel is a
  // PUBLIC marketing prop only. Signed-in users (/welcome) must never see
  // sample rows — they get their live items, a loading shimmer, or a calm
  // "all clear" state.
  const liveState = !isAuthed ? 'demo' : hasLiveData ? 'live' : liveItems === null ? 'loading' : 'empty';

  // Map live items into the row shape the renderer uses. Demo rows have a
  // null `itemType` so the renderer skips the icon & click handler — keeping
  // one map() path for both modes. `tier` is computed once here.
  const activeDeadlines = liveState === 'live'
    ? liveItems.map((it) => ({
        id: it.id,
        itemType: it.itemType,
        title: it.title,
        workspace: it.businessName || 'Personal',
        due: formatHoursUntil(it.hoursUntil),
        amountLabel: it.itemType === 'payment' && it.amountMinor != null ? formatAmount(minorToMajor(it.amountMinor, it.currency), it.currency) : null,
        tier: tierOf(it.hoursUntil),
      }))
    : liveState === 'demo'
      ? DEMO_DEADLINE_SETS[setIndex].map((it) => ({
          ...it,
          itemType: null,
          amountLabel: null,
          tier: tierOf(it.hoursUntil),
        }))
      : [];

  // "+ N more biting today" chip — visible only when the live `urgent` total
  // exceeds the urgent rows already shown in the splash. Demo mode never
  // renders the chip — the public landing isn't authoritative about workload.
  const shownUrgentCount = hasLiveData
    ? activeDeadlines.filter((row) => row.tier === 'urgent').length
    : 0;
  const hiddenUrgent = hasLiveData && liveTotals
    ? Math.max(0, (liveTotals.urgent || 0) - shownUrgentCount)
    : 0;

  // Row click → deep-link into the matching view with `?selected=<id>`, which
  // is consumed by useDeepLinkSelection / inline TasksView effect. Demo rows
  // are non-interactive. Set the landing timestamp first so the user doesn't
  // bounce straight back to /welcome.
  const handleRowClick = (item) => {
    if (!item.itemType) return;
    const meta = ITEM_TYPE_META[item.itemType];
    if (!meta) return;
    safeStorage.setString(LANDING_TIMESTAMP_KEY, String(Date.now()));
    navigate(`${meta.route}?selected=${encodeURIComponent(item.id)}`);
  };

  // Memoise the current month label so the panel always feels current.
  const monthLabel = useMemo(
    () => new Date().toLocaleString('en-US', { month: 'long', year: 'numeric' }),
    [],
  );

  const handlePrimaryCta = () => {
    if (isAuthed) navigate('/', { replace: true });
    else navigate('/login');
  };

  return (
    <div data-testid="landing-page" className="min-h-screen w-full relative" style={{ background: 'var(--bg-canvas)' }}>
      {/* Subtle warm mesh — same as authed app for brand continuity */}
      <div aria-hidden className="absolute inset-0 pointer-events-none" style={{ background: 'var(--bg-canvas-mesh)' }} />

      {/* ── Topbar — brand + theme toggle + Sign in ─────────────────────── */}
      <header className="relative z-10 px-6 md:px-10 lg:px-16 pt-6 md:pt-8 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <BrandMark variant="mark" size="md" testId="landing-brand-mark" />
          <div className="pd-brand-wordmark" style={{ fontSize: 17 }}>
            PolkDes<span className="pd-brand-wordmark-q">Q</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="landing-theme-toggle"
            type="button"
            aria-label={isDark ? 'Switch to light theme' : 'Switch to dark theme'}
            onClick={toggleTheme}
            className="pd-icon-button pd-btn-press"
          >
            {isDark ? <SunMedium className="w-4.5 h-4.5" /> : <MoonStar className="w-4.5 h-4.5" />}
          </button>
          <button
            data-testid="landing-signin-button"
            type="button"
            onClick={handlePrimaryCta}
            className="pd-btn-press inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-[13px] font-semibold transition"
            style={{ background: 'var(--pd-text)', color: 'var(--bg-canvas)' }}
          >
            {isAuthed ? 'Open dashboard' : 'Sign in'}
            <ArrowUpRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </header>

      {/* ── Hero ────────────────────────────────────────────────────────── */}
      <main className="relative z-10 mx-auto max-w-3xl px-6 md:px-10 pt-20 md:pt-32 pb-24">
        <div className="text-center">
          <span
            data-testid="landing-eyebrow"
            className="inline-block text-[11px] uppercase tracking-[0.18em] font-semibold"
            style={{ color: 'var(--pd-muted)' }}
          >
            Daily planner · {monthLabel}
          </span>
          <h1
            data-testid="landing-headline"
            className="mt-6 text-4xl sm:text-5xl lg:text-[64px] font-medium leading-[0.95]"
            style={{
              fontFamily: 'var(--font-display)',
              color: 'var(--pd-text)',
              letterSpacing: '-0.045em',
            }}
          >
            Nothing slips when<br/>everything has a desk.
          </h1>
          <p
            data-testid="landing-subhead"
            className="mt-7 text-[15px] sm:text-base max-w-xl mx-auto"
            style={{ color: 'var(--pd-muted)', lineHeight: 1.75 }}
          >
            PolkDesQ surfaces what&apos;s due, who you owe, and which deadline is about to bite —
            across every business you run.
          </p>
        </div>

        {/* ── Feature showcase — deadlines in 48 hours ────────────────── */}
        <div
          data-testid="landing-deadlines-panel"
          className="mt-16 mx-auto max-w-xl rounded-[24px] overflow-hidden"
          style={{
            background: 'var(--pd-popover)',
            border: '1px solid var(--pd-popover-border)',
            boxShadow: '0 24px 60px rgba(0,0,0,0.18)',
          }}
        >
          <div className="px-6 pt-5 pb-4 flex items-baseline justify-between" style={{ borderBottom: '1px solid var(--pd-border)' }}>
            <div>
              <div className="text-[15px] font-semibold tracking-tight" style={{ color: 'var(--pd-text)' }}>
                {liveState === 'demo' ? 'Expiring in the next 48 hours' : 'Your next 48 hours'}
              </div>
              <div
                data-testid="landing-deadlines-ticker"
                className="text-[11px] mt-1 font-mono tabular-nums"
                style={{ color: 'var(--pd-muted)' }}
              >
                {liveState === 'live' ? (
                  <>
                    <span className="inline-block w-1.5 h-1.5 rounded-full pd-deadline-dot mr-1.5" style={{ background: 'var(--pd-brand)', verticalAlign: 'middle' }} />
                    Live · pulled{' '}
                    <span key={secondsAgo} className="pd-ticker-digit" style={{ color: 'var(--pd-text)' }}>
                      {secondsAgo}
                    </span>
                    {' '}{sLabel} ago
                  </>
                ) : liveState === 'demo' ? (
                  <>
                    Updated{' '}
                    <span key={secondsAgo} className="pd-ticker-digit" style={{ color: 'var(--pd-text)' }}>
                      {secondsAgo}
                    </span>
                    {' '}{sLabel} ago · refreshes every {ROTATION_SECONDS}s
                  </>
                ) : liveState === 'loading' ? (
                  'Checking your deadlines…'
                ) : (
                  <>
                    <span className="inline-block w-1.5 h-1.5 rounded-full mr-1.5" style={{ background: 'var(--pd-green)', verticalAlign: 'middle' }} />
                    Live · nothing expiring
                  </>
                )}
              </div>
            </div>
            {liveState !== 'loading' && (
              <span className="inline-flex items-center gap-1 text-[11px] font-mono tabular-nums" style={{ color: 'var(--pd-brand)' }}>
                <span className="w-1.5 h-1.5 rounded-full pd-deadline-dot" style={{ background: 'var(--pd-brand)', animationDelay: '0s' }} />
                {activeDeadlines.length}
              </span>
            )}
          </div>
          {liveState === 'loading' ? (
            <div data-testid="landing-deadlines-loading" className="px-5 py-4 space-y-2.5">
              {[1, 2, 3].map((i) => <div key={i} className="h-9 rounded-xl skeleton-shimmer" />)}
            </div>
          ) : liveState === 'empty' ? (
            <div data-testid="landing-deadlines-empty" className="px-6 py-10 text-center">
              <div className="text-[13px] font-medium" style={{ color: 'var(--pd-text)' }}>Nothing expiring in the next 48 hours.</div>
              <p className="text-[11px] mt-1.5" style={{ color: 'var(--pd-muted)' }}>You&apos;re clear — open the dashboard to plan ahead.</p>
            </div>
          ) : (
            <ul
              key={liveState === 'live' ? 'live' : `set-${setIndex}`}
              data-testid={liveState === 'live' ? 'landing-deadlines-set-live' : `landing-deadlines-set-${setIndex}`}
              className="px-3 py-2 pd-deadline-list"
            >
              {activeDeadlines.map((item, idx) => (
                <LandingDeadlineRow
                  key={briefingItemKey(item)}
                  item={item}
                  idx={idx}
                  isLast={idx === activeDeadlines.length - 1}
                  meta={item.itemType ? ITEM_TYPE_META[item.itemType] : null}
                  onRowClick={handleRowClick}
                />
              ))}
            </ul>
          )}

          {/* Rotation progress dots — only meaningful when the demo carousel
              is cycling. Live data has one set so dots would be misleading. */}
          {liveState !== 'demo' ? (
            <div className="px-4 pb-3 pt-1" aria-hidden />
          ) : (
            <div
              data-testid="landing-rotation-progress"
              className="px-4 pb-3 pt-1 flex items-center justify-center gap-1.5"
              aria-hidden
            >
              {ROTATION_DOT_THRESHOLDS.map((threshold) => (
                <span
                  key={`rotation-dot-${threshold}`}
                  className={`pd-rotation-dot ${secondsAgo >= threshold ? 'is-lit' : ''}`}
                />
              ))}
            </div>
          )}
        </div>

        {/* "+ N more biting today" — only when live totals exceed displayed urgent rows */}
        {hiddenUrgent > 0 && (
          <div className="mt-5 flex justify-center">
            <button
              data-testid="landing-more-urgent-chip"
              type="button"
              onClick={() => {
                safeStorage.setString(LANDING_TIMESTAMP_KEY, String(Date.now()));
                navigate('/briefing');
              }}
              className="pd-btn-press pd-more-urgent-chip inline-flex items-center gap-2 rounded-full px-4 py-2 text-[12px] font-semibold"
            >
              <span aria-hidden className="w-1.5 h-1.5 rounded-full pd-deadline-dot pd-deadline-dot--urgent" />
              + {hiddenUrgent} more biting today
              <ArrowUpRight className="w-3.5 h-3.5" />
            </button>
          </div>
        )}

        {/* ── Single CTA ──────────────────────────────────────────────── */}
        <div className="mt-16 flex flex-col items-center gap-4">
          <button
            data-testid="landing-cta-signin"
            type="button"
            onClick={handlePrimaryCta}
            className="pd-btn-press inline-flex items-center gap-2 rounded-full px-6 py-3 text-[14px] font-semibold transition"
            style={{
              background: 'var(--pd-brand)',
              color: 'var(--pd-on-brand, #0B0E14)',
              boxShadow: '0 12px 28px rgba(245, 158, 11, 0.32)',
            }}
          >
            {isAuthed ? 'Open dashboard' : 'Sign in to your desk'}
            <ArrowUpRight className="w-4 h-4" />
          </button>
          {isAuthed ? (
            <p
              data-testid="landing-auto-advance-countdown"
              className="text-[11px] font-mono tabular-nums"
              style={{ color: 'var(--pd-subtle)' }}
            >
              Opening dashboard in {autoAdvanceLeft}s
            </p>
          ) : (
            <p className="text-[11px]" style={{ color: 'var(--pd-subtle)' }}>
              Microsoft SSO · email · invite-only workspace
            </p>
          )}
        </div>
      </main>

      {/* ── Single classy line at the bottom ─────────────────────────── */}
      <footer className="relative z-10 px-6 md:px-10 pb-10 text-center">
        <p className="text-[11px]" style={{ color: 'var(--pd-subtle)' }}>
          Built for the people whose phone never stops ringing.
        </p>
      </footer>
    </div>
  );
}
